import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Spinner } from "@/components/ui/spinner";
import ConfirmationModal from "components/modals/confirmation-modal";
import { TooltipWrapper } from "components/permissions/permissions";
import { User, useIdentityContext } from "contexts/identity-context";
import * as QueryKeys from "data";
import { RejectedInvite, UserMembership } from "data/models";
import {
  userAcceptInvitation,
  userRejectInvitation,
} from "data/mutations-common";
import { fetchUserMemberships } from "data/queries-common";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

interface Props {
  userState: User;
  setHasHandledInvitations: React.Dispatch<React.SetStateAction<boolean>>;
  children?: React.ReactNode;
}

interface Message {
  body: string;
  severity: "success" | "info" | "error";
}

export const UserInvitationsOnBoarding = (props: Props) => {
  const { user: identityUser, refreshIdentity } = useIdentityContext();
  const user = identityUser as User;

  const handleDeleteClose = () => {
    setDeleteOpen(false);
  };

  const initialUserInvitation: UserMembership = {
    organisationId: "",
    userMemberId: "",
    status: "",
    organisationName: "",
    userFullName: "",
    userEmail: "",
    organisationEmail: "",
    userFirstName: "",
    userLastName: "",
  };

  const [invitation, setInvitation] =
    useState<UserMembership>(initialUserInvitation);

  const handleDelete = (invitation: UserMembership) => {
    setInvitation(invitation);
    setOrganisationName(invitation.organisationName);
    setDeleteOpen(true);
  };
  const [organisationName, setOrganisationName] = useState("");

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [messages, setMessages] = useState<Message | null>(null);

  const queryClient = useQueryClient();
  const membershipsQuery = useQuery({
    queryKey: [QueryKeys.userMembership, user.cognitoSub],
    queryFn: () => fetchUserMemberships(true),
  });

  const acceptInvitationMutation = useMutation({
    mutationFn: (organisationId: string) =>
      userAcceptInvitation(organisationId),
    onSuccess: async (newMembership) => {
      setMessages({
        severity: "success",
        body: `Invitation from "${newMembership.organisationName}" has been accepted`,
      });

      let pendingCount = 0;
      queryClient.setQueryData<UserMembership[]>(
        [QueryKeys.userMembership, user.cognitoSub],
        (oldMemberships) => {
          if (!oldMemberships) return [newMembership];
          const newState = oldMemberships.map((membership) => {
            if (membership.organisationId === newMembership.organisationId) {
              return newMembership;
            }
            return membership;
          });
          pendingCount = newState.filter(
            (x) => x.status === "Pending",
          ).length;
          return newState;
        },
      );

      await refreshIdentity();
      props.setHasHandledInvitations(pendingCount === 0);
    },
  });

  const rejectInvitationMutation = useMutation({
    mutationFn: (organisationId: string) =>
      userRejectInvitation(organisationId),
    onSuccess: (rejectedMembership: RejectedInvite) => {
      setMessages({
        severity: "info",
        body: `Invitation from "${invitation.organisationName}" has been rejected`,
      });
      handleDeleteClose();

      let pendingCount = 0;
      queryClient.setQueryData<UserMembership[]>(
        [QueryKeys.userMembership, user.cognitoSub],
        (oldMemberships) => {
          if (!oldMemberships) return [];
          const newState = oldMemberships.filter(
            (membership) =>
              membership.organisationId !==
              rejectedMembership.organisationId,
          );
          pendingCount = newState.filter(
            (x) => x.status === "Pending",
          ).length;
          props.setHasHandledInvitations(pendingCount === 0);
          return newState;
        },
      );

    },
  });

  const rows = !membershipsQuery.isSuccess
    ? []
    : membershipsQuery.data
        .filter((invitation) => invitation.status === "Pending")
        .map((invitation): any => ({
          id: invitation.organisationName,
          invitation: invitation,
          organisationName: invitation.organisationName,
          actions: invitation.status,
        }))
        .sort((a, b) =>
          a.organisationName
            .toUpperCase()
            .localeCompare(b.organisationName.toUpperCase()),
        );

  const invitations = rows;

  return (
    <>
      {messages && (
        <Alert
          variant={messages.severity === "error" ? "destructive" : "default"}
        >
          <AlertDescription>{messages.body}</AlertDescription>
        </Alert>
      )}

      <ConfirmationModal
        isLoading={membershipsQuery.isPending}
        isError={membershipsQuery.isError}
        open={deleteOpen}
        header={`Reject this invitation?`}
        body={`You won't be able to join "${organisationName}" again unless you receive another invitation.`}
        leftButtonText={"CANCEL"}
        leftButtonColor="primary"
        rightButtonText={"REJECT INVITATION"}
        rightButtonColor="error"
        handleClose={() => handleDeleteClose()}
        handleLeftButton={() => handleDeleteClose()}
        isRightButtonLoading={rejectInvitationMutation.isPending}
        handleRightButton={() => {
          rejectInvitationMutation.mutate(invitation.organisationId);
        }}
      />

      <div className="flex flex-col w-full gap-4">
        <p className="text-sm">
          {!membershipsQuery.isPending && rows.length == 0
            ? "No pending invitations"
            : "Pending organisation invitations"}
        </p>
        {props.children}
        {invitations.length > 0 && (
          <div className="flex flex-col gap-4">
            {membershipsQuery.isPending ? (
              <Progress value={undefined} />
            ) : (
              invitations.map((item: any) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between"
                >
                  <TooltipWrapper
                    title={item.organisationName}
                    style={{ width: "50%", textAlign: "left" }}
                  >
                    <span className="text-sm truncate">
                      {item.organisationName}
                    </span>
                  </TooltipWrapper>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-success text-success-foreground hover:bg-success/80"
                      onClick={() =>
                        acceptInvitationMutation.mutate(
                          item.invitation.organisationId,
                        )
                      }
                      disabled={acceptInvitationMutation.isPending}
                    >
                      {acceptInvitationMutation.isPending ? (
                        <Spinner />
                      ) : (
                        "Accept"
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(item.invitation)}
                      disabled={rejectInvitationMutation.isPending}
                    >
                      {rejectInvitationMutation.isPending ? (
                        <Spinner />
                      ) : (
                        "Reject"
                      )}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </>
  );
};
